import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Stack, useMediaQuery, useTheme, Fab, CircularProgress } from '@mui/material'
import TopBar from './TopBar/TopBar'
import { TranslatorProvider } from '../translation/TranslatorProvider'
import TranslatorWidget from '../translation/TranslatorWidget'
import LeftMenuContainer from './LeftMenuContainer'
import MonitoringScreenByGroup from '../monitoring/MonitoringScreenByGroup'
import MicHome from '../home/MicHome'
import Footer from './Footer'
import VisionGuard from '../vision/VisionGuard'
import VisionIncidents from '../vision/VisionIncidents'
import VisionStartupGuard from '../vision/VisionStartupGuard'
import SensitiveScreen from '../vision/SensitiveScreen'
import ProtectedRouteContent from '../vision/ProtectedRouteContent'
import { MenuOutlined } from '@mui/icons-material'

import MainStorage from '../routes/MainStorage'
import MainStoragePosition from '../routes/MainStoragePosition'

import MainPreset from '../routes/MainPreset'
import MainInspection from '../routes/MainInspection'

import MainDefault from '../routes/MainDefault'
import { Navigate, Route, Routes } from 'react-router'
import SmartMaterialUITableTester from '../generic/SmartMaterialUITableTester'
import SmartMaterialUITableTest from '../generic/SmartMaterialUITableTest'
import DeviceSettings from '../configuration/devices/DeviceSettingsLayout'
import ShelveStoragePositions from '../configuration/storage/ShelveStoragePositions'
import MainReference from '../routes/MainReference'
import MainFixture from '../routes/MainFixture.jsx'
import MainRawMaterialReference from '../routes/MainRawMaterialReference'
import MainBomLibrary from '../routes/MainBomLibrary'
import MainDataCorrection from '../routes/MainDataCorrection'
import MainDocumentRepository from '../routes/MainDocumentRepository'
import MainPrinters from '../routes/MainPrinters'
import BomLibraryDetail from '../configuration/bomLibrary/BomLibraryDetail'
import MainSensorsActuators from '../routes/MainSensorsActuators.jsx'
import SensorsActuatorsComponentsEdit from '../configuration/Sensor&Actuators/SensorsActuatorsComponentsEdit'
import MainDevices from '../routes/MainDevices'
import MainFixtureTypes from '../routes/MainFixtureTypes'
import MainWorkflows from '../routes/MainWorkflows'
import MainTechnology from '../routes/MainTechnology'
import Notifications from '../maintenance/alarms&notifications/notifications/Notifications'
import Alarms from '../maintenance/alarms&notifications/alarms/Alarms'
import PresenceDetection from '../maintenance/presenceDetection/PresenceDetectionVideo'
import MainConfigurationTools from '../routes/MainConfigurationTools'
import TestNewDevice from '../generic/TestNewDevice'
import MainToolManager from '../routes/MainToolManager'
import DisplayThirdPartiesSoftware from '../dashboard/DisplayThirdPartiesSoftware'
import MainPlanner from '../routes/MainPlanner'
import MainOrders from '../routes/MainOrders'
import ToolReferenceSettings from '../configuration/tools/ToolReferenceSettings'
import ToolInstanceSettings from '../configuration/tools/ToolInstanceSettings'
import MainCalendars from './../routes/MainCalendars'
import MainCalendarsEdit from './../routes/MainCalendarEdit'
import MainPicklist from './../routes/MainPicklist'
import MainWorkers from '../routes/MainWorkers'
import WorkerSettings from '../configuration/workers/WorkerSettings'
import MainMaterials from '../routes/MainMaterials.jsx'
import LoginPage from '../routes/LoginPage'
import { activityDetector } from '../generic/utilities/ActivityDetector'
import MainProfiles from '../routes/MainProfiles'
import MainUsers from '../routes/MainUsers'
import MainRule from '../routes/MainRule'
import MainIncidence from '../routes/MainIncidence'
import MainSettings from '../routes/MainSettings'
import { useUser } from '../generic/hooks/useUser'
import MainDeviceStatus from '../routes/MainDeviceStatus.jsx'
import MainOrderStatus from '../routes/MainOrderStatus.jsx'
import MainReason from '../routes/MainReason.jsx'
import MainQualityTypes from '../routes/MainQualityTypes.jsx'
import MainFabricationOrderStatus from '../routes/MainFabricationOrderStatus.jsx'
import { FULL_HEIGHT } from '../generic/utilities/size_utils.js'
import MainSequencer from '../routes/MainSequencer.jsx'
import MainAIPlanner from '../routes/MainAIPlanner.jsx'
import MainOtherTask from '../routes/MainOtherTask.jsx'
import NoLicense from '../routes/NoLicense.jsx'
import MainFeatures from '../routes/MainFeatures.jsx'
import MainBackoffice, { BACKOFFICE_CAMERA_CONTROL_TAB } from '../routes/MainBackoffice.jsx'
import MainDashboardOEE from '../routes/MainDashboardOEE.jsx'
import MainDashboardProduction from '../routes/MainDashboardProduction.jsx'
import MainDashboardPlanning from '../routes/MainDashboardPlanning.jsx'
import MainDashboardTooling from '../routes/MainDashboardTooling.jsx'
import MainDashboardCustomized from '../routes/MainDashboardCustomized.jsx'
import MainDashboardProxied from '../routes/MainDashboardProxied.jsx'
import useLicensePlanner from '../generic/hooks/useLicensePlanner/useLicensePlanner.jsx'
import MainQuality from '../routes/MainQuality.jsx'
import MainQualityProductAcceptance from '../routes/MainQualityProductAcceptance.jsx'
import MainCostumer from '../routes/MainCustomer.jsx'
import DeviceBooker from '../debug/DeviceBooker.jsx'
import MainMaterialReception from '../routes/MainMaterialReception.jsx'
import MainMos from '../Mos/MainMos.jsx'
import MainMaterialReceptionIncidence from '../routes/MainMaterialReceptionIncidence.jsx'
import MainClusterRequests from '../maintenance/actions/MainClusterRequests.jsx'
import ActionCenter from '../maintenance/actions/ActionCenter.jsx'
import ActionCenterMenu from '../maintenance/actions/ActionCenterMenu.jsx'
import MainStorageTraceability from '../routes/MainStorageTraceability'
import useFrontEndConfig from '../generic/hooks/useFrontEndConfig/useFrontEndConfig.js'
import { startCellStatusPolling, stopCellStatusPolling } from '../generic/CellGeneralStatus.jsx'
import { UnsavedChangesGuard } from '../generic/utilities/unsavedChangesGuard'
import MainMenuPermissionGuard from './MainMenuPermissionController.jsx'
import PendingCellMoves from '../debug/PendingCellMoves.jsx'
import DeviceGroupSettings from '../configuration/deviceGroups/DeviceGroupSettings.jsx'
import useUserLanguage from '../generic/hooks/useUserLanguage/useUserLanguage'
import CameraControl from '../vision/cameraControl/CameraControl'

const MainContainer = () => {
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('md'))
  const [hideMenu, setHideMenu] = useState(isSmall)
  const [minimizeDrawer, setMinimizeDrawer] = useState(false)
  const [url, setUrl] = useState('')
  const [loginPageTimeoutAlert, setLoginPageTimeoutAlert] = useState(false)
  const { user, userLogout, getLicenseStatus } = useUser()
  // Historia 138: resuelve, carga y aplica el idioma del usuario ANTES de montar el arbol
  // autenticado. Montarlo antes le ensena al usuario los textos estandar y un instante despues los
  // de su planta, que es el parpadeo que la historia prohibe anadir.
  const { ready: languageReady, catalogue: languageCatalogue, changeLanguage: applyLanguage, adoptCatalogue: adoptLanguageCatalogue } = useUserLanguage(user)
  const [reloadTopBar, setReloadTopBar] = useState(false)
  const [hideMainMenuBar, setHideMainMenuBar] = useState(false)
  const licensePlanner = useLicensePlanner()
  const frontEndConfig = useFrontEndConfig()[0]

  const myActivityDetector = useMemo(() => {
    const timeToIdle = frontEndConfig?.activeSessionTime ?? 4 * 60 * 60 * 1000 // Milliseconds, default to 4 hours

    return activityDetector({
      timeToIdle,
      autoInit: false
    })
  }, [frontEndConfig?.activeSessionTime])

  const handleIdle = useCallback(() => {
    userLogout()
    setLoginPageTimeoutAlert(true)
    setTimeout(() => {
      setLoginPageTimeoutAlert(false)
    }, 100)
  }, [userLogout])

  const handleActive = useCallback(() => {
    setLoginPageTimeoutAlert(false)
  }, [])

  useEffect(() => {
    if (!user) {
      myActivityDetector.stop()
      stopCellStatusPolling()
      return
    }

    // Activity detector
    myActivityDetector.init()
    const offIdle = myActivityDetector.on('idle', handleIdle)
    const offActive = myActivityDetector.on('active', handleActive)

    // Cell status polling
    startCellStatusPolling()

    return () => {
      offIdle()
      offActive()
      myActivityDetector.stop()
      stopCellStatusPolling()
    }
  }, [myActivityDetector, handleIdle, handleActive, user])

  const contentStyle = {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    paddingTop: '1rem'
  }
  const footerStyle = {
    backgroundColor: 'black',
    position: 'relative',
    bottom: 0,
    right: 0,
    left: 0
  }

  const LeftMenuButton = () => {
    if (hideMainMenuBar) return null

    return (
      <Stack
        sx={{
          direction: 'row',
          justifyContent: 'end',
          position: 'absolute',
          top: 88,
          left: 10,
          zIndex: '1300'
        }}
      >
        <Fab
          size='small'
          disabled={false}
          onClick={() => setHideMenu(!hideMenu)}
          color='primary'
          sx={{
            color: 'white',
            backgroundColor: theme => theme.palette.background.color_212121,
            opacity: !hideMenu ? 0.6 : 0.8
          }}
        >
          <MenuOutlined />
        </Fab>
      </Stack>
    )
  }
  const getPlannerComponent = () => {
    switch (licensePlanner) {
      case 'SEQUENCER':
        return (<MainSequencer />)
      case 'PLANNER':
        return (<MainPlanner />)
      case 'AIPLANNER':
        return (<MainAIPlanner />)
      default:
        return (<></>)
    }
  }

  // Story 213 (S142): with the screen covered, loading the user gets a 423 and never ends; the start-up
  // guard covers and keeps the camera vigilance going meanwhile. Otherwise it renders nothing, as before.
  if (user === undefined) {
    return <VisionStartupGuard />
  }

  if (getLicenseStatus === null) {
    return <NoLicense licenseStatus={null} />
  }
  return (
    getLicenseStatus !== 'VALID'
      ? <NoLicense licenseStatus={getLicenseStatus} />
      : !user
          ? <LoginPage timeoutAlert={loginPageTimeoutAlert} />
          // Historia 138 (S34): nada del árbol autenticado se pinta hasta que el idioma está
          // resuelto, cargado y aplicado. No puede quedarse aquí: las dos lecturas tragan sus
          // propios fallos -- sin catálogo quedan los ocho de siempre, sin diccionario queda el
          // idioma base -- así que siempre acaba resolviéndose.
          : !languageReady
              // Se retiene el árbol, pero NO se pinta la nada: un usuario delante de una pantalla en
              // blanco no puede distinguir "cargando" de "roto", y el 18/09 eso costó diez minutos de
              // no saber qué pasaba. El indicador no enseña ni una palabra de la interfaz, así que S34
              // se sigue cumpliendo: lo que prohíbe es pintar el árbol autenticado, no decir que se
              // está cargando.
              ? (
                <Stack alignItems='center' justifyContent='center' sx={{ height: '100vh' }}>
                  <CircularProgress />
                </Stack>
                )
              : (
                <TranslatorProvider>
                  {/* Story 213: camera vigilance guard. Above top bar, menu and routes, so covering the
                      workstation unmounts all of MIC content (RN21); not a MOS widget (CU10). With the scope
                      ALL_MIC it covers and turns the camera on as MIC is entered (C13.7, S262). Covered, an ADMIN or
                      SUPERADMIN gets Camera control on its own (C14.3, S285). */}
                  <VisionGuard visionEnabled={frontEndConfig?.vision?.enabled === true} scope={frontEndConfig?.vision?.scope} unwatchedShowsProtected={frontEndConfig?.vision?.unwatchedShowsProtected === true} coveredPage={{ path: '/backoffice/cameraControl', element: <CameraControl /> }}>
                    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      <UnsavedChangesGuard />
                      <MainMenuPermissionGuard
                        isSmall={isSmall}
                        setHideMenu={setHideMenu}
                        setHideMainMenuBar={setHideMainMenuBar}
                      />
                      <TopBar
                        languageCatalogue={languageCatalogue}
                        applyLanguage={applyLanguage}
                        setUrl={setUrl}
                        changeHideMenu={() => setHideMenu(!hideMenu)}
                        hideMainMenuButton={hideMainMenuBar}
                        reloadTopBar={reloadTopBar}
                      />
                      <TranslatorWidget />
                      <div style={{ display: 'flex', flexGrow: 1 }}>
                        {!isSmall
                          ? <LeftMenuButton />
                          : null}
                        <LeftMenuContainer
                          id='left-menu-container'
                          hideMenu={hideMenu}
                          setHideMenu={setHideMenu}
                          minimizeDrawer={minimizeDrawer}
                          setMinimizeDrawer={setMinimizeDrawer}
                          url={url}
                        />
                        <div style={{ overflow: 'auto', height: FULL_HEIGHT.WITH_NAV_BAR, flexGrow: 1 }}>
                          <Stack direction='column' sx={{ height: '100%', minWidth: 'min-content' }}>
                            <div style={{ ...contentStyle }}>
                              {/* Story 213 (S336): a screen known protected only by its 423 while waiting is the placeholder. */}
                              <ProtectedRouteContent>
                                <Routes>
                                  <Route path='/home' element={<MicHome />} />
                                  <Route path='/' element={<></>} />

                                  <Route path='/profiles' element={<MainProfiles />} />
                                  <Route path='/users' element={<MainUsers />} />
                                  <Route path='/settings' element={<MainSettings setReloadTopBar={setReloadTopBar} reloadTopBar={reloadTopBar} />} />
                                  <Route path='/settings/cameraControl' element={<Navigate to='/backoffice/cameraControl' replace />} />

                                  <Route path='/backoffice/proxiedServices' element={<MainBackoffice onLanguageCatalogueChanged={adoptLanguageCatalogue} />} />
                                  <Route path='/backoffice/cameraControl' element={<MainBackoffice activePath={BACKOFFICE_CAMERA_CONTROL_TAB} onLanguageCatalogueChanged={adoptLanguageCatalogue} />} />

                                  <Route path='/monitoring' element={<MonitoringScreenByGroup />} />

                                  <Route path='/planning' element={<SensitiveScreen screen='PLANNER'>{getPlannerComponent()}</SensitiveScreen>} />

                                  <Route path='/process' element={<MainDefault />} />
                                  <Route path='/process/workflow2' element={<SensitiveScreen screen='WORKFLOW'><MainWorkflows activePath={0} /></SensitiveScreen>} />
                                  <Route path='/process/programs' element={<MainWorkflows activePath={1} />} />
                                  <Route path='/process/processTraceability' element={<MainWorkflows activePath={2} />} />
                                  <Route path='/process/balanced' element={<MainWorkflows activePath={3} />} />
                                  <Route path='/process/rules' element={<MainRule activePath={0} />} />
                                  <Route path='/process/formulas' element={<MainRule activePath={1} />} />
                                  <Route path='/process/storage' element={<MainStorage />} />
                                  <Route path='/process/storagePosition/:storageId' element={<MainStoragePosition />} />
                                  <Route path='/process/storageTraceability/:storageId' element={<MainStorageTraceability />} />

                                  <Route path='/process/actionMenu' element={<ActionCenterMenu />} />
                                  <Route path='/process/action' element={<ActionCenter activePath='action' />} />
                                  <Route path='/process/command' element={<ActionCenter activePath='command' />} />
                                  <Route path='/process/pendingRequests' element={<ActionCenter activePath='pendingRequests' />} />
                                  <Route path='/process/command/byLocation' element={<ActionCenter activePath='command/byLocation' />} />
                                  <Route path='/process/command/byResource' element={<ActionCenter activePath='command/byResource' />} />

                                  <Route path='/orders' element={<MainOrders activePath={0} />} />
                                  <Route path='/fabricationOrders' element={<MainOrders activePath={1} />} />

                                  <Route path='/resources' element={<MainDefault />} />
                                  <Route path='/resources/reference' element={<MainReference />} />
                                  <Route path='/resources/toolManager' element={<MainToolManager activePath={0} />} />
                                  <Route path='/resources/toolTables' element={<MainToolManager activePath={1} />} />
                                  <Route path='/resources/toolManager/:toolId' element={<ToolReferenceSettings />} />
                                  <Route path='/resources/toolManager/:toolReferenceId/toolInstance/:toolId' element={<ToolInstanceSettings />} />

                                  <Route path='/resources/rawmaterialReference' element={<MainRawMaterialReference />} />
                                  <Route path='/resources/bomLibrary' element={<MainBomLibrary />} />
                                  <Route path='/resources/bomLibrary/:code' element={<BomLibraryDetail />} />
                                  <Route path='/resources/fixtures' element={<MainFixture />} />
                                  <Route path='/resources/fixtureReference' element={<MainFixture activePath={0} />} />
                                  <Route path='/resources/fixtureAssemblyReference' element={<MainFixture activePath={1} />} />

                                  <Route path='/dataAnalytics' element={<MainDefault />} />
                                  <Route path='/dataAnalytics/dashboard' element={<MainDefault />} />
                                  <Route path='/dataAnalytics/dashboard/oee' element={<MainDashboardOEE />} />
                                  <Route path='/dataAnalytics/dashboard/production' element={<MainDashboardProduction activePath={0} />} />
                                  <Route path='/dataAnalytics/dashboard/production/device' element={<MainDashboardProduction activePath={1} />} />
                                  <Route path='/dataAnalytics/dashboard/production/wip' element={<MainDashboardProduction activePath={2} />} />
                                  <Route path='/dataAnalytics/dashboard/production/quality' element={<MainDashboardProduction activePath={3} />} />
                                  <Route path='/dataAnalytics/dashboard/planning' element={<MainDashboardPlanning activePath={0} />} />
                                  <Route path='/dataAnalytics/dashboard/planning/delayed' element={<MainDashboardPlanning activePath={1} />} />
                                  <Route path='/dataAnalytics/dashboard/planning/status' element={<MainDashboardPlanning activePath={2} />} />
                                  <Route path='/dataAnalytics/dashboard/planning/planned' element={<MainDashboardPlanning activePath={3} />} />
                                  <Route path='/dataAnalytics/dashboard/tooling' element={<MainDashboardTooling activePath={0} />} />
                                  <Route path='/dataAnalytics/dashboard/tooling/operational' element={<MainDashboardTooling activePath={0} />} />
                                  <Route path='/dataAnalytics/dashboard/tooling/traceability' element={<MainDashboardTooling activePath={1} />} />
                                  <Route path='/dataAnalytics/dashboard/tooling/economic' element={<MainDashboardTooling activePath={2} />} />
                                  <Route path='/dataAnalytics/dashboard/customized' element={<MainDashboardCustomized />} />
                                  <Route path='/dataAnalytics/dashboard/proxied/:prefix' element={<MainDashboardProxied />} />
                                  <Route path='/dataAnalytics/prediction' element={<MainDefault />} />

                                  <Route path='/reports' element={<MainDefault />} />
                                  <Route path='/dataAnalytics/reports/inspection' element={<MainInspection />} />
                                  <Route path='/dataAnalytics/reports/preset' element={<MainPreset />} />
                                  <Route path='/dataAnalytics/reports/qualityReport' element={<MainDefault />} />

                                  <Route path='/configuration' element={<MainDefault />} />
                                  <Route path='/configuration/assets' element={<MainDevices activePath={0} />} />
                                  <Route path='/configuration/assetGroups' element={<MainDevices activePath={1} />} />
                                  <Route path='/configuration/fixtureTypes' element={<MainFixtureTypes />} />
                                  <Route path='/configuration/sensorsActuators' element={<MainSensorsActuators activePath={0} />} />
                                  <Route path='/configuration/sensorsActuatorsComponents' element={<MainSensorsActuators activePath={1} />} />
                                  <Route
                                    path='/configuration/sensorsActuatorsComponents/:sensorActuatorComponentId'
                                    element={<SensorsActuatorsComponentsEdit />}
                                  />

                                  <Route path='/configuration/customer' element={<MainCostumer />} />
                                  <Route path='/configuration/technology' element={<MainTechnology activePath={0} />} />
                                  <Route path='/configuration/technologyGroups' element={<MainTechnology activePath={1} />} />
                                  <Route path='/configuration/toolGroups' element={<MainConfigurationTools activePath={0} />} />
                                  <Route path='/configuration/toolTypes' element={<MainConfigurationTools activePath={1} />} />
                                  <Route path='/configuration/toolParameters' element={<MainConfigurationTools activePath={2} />} />
                                  <Route path='/configuration/assets/:deviceId' element={<DeviceSettings />} />
                                  <Route path='/configuration/assets/:deviceId/shelve/:shelveId' element={<ShelveStoragePositions />} />
                                  <Route path='/configuration/assetGroups/:groupId' element={<DeviceGroupSettings />} />
                                  <Route path='/configuration/calendars' element={<MainCalendars />} />
                                  <Route path='/configuration/calendar/:calendarId' element={<MainCalendarsEdit isReference />} />
                                  <Route path='/configuration/calendarInstance/:calendarId' element={<MainCalendarsEdit isReference={false} />} />
                                  <Route path='/configuration/features' element={<MainFeatures />} />
                                  <Route path='/configuration/picklist' element={<MainPicklist />} />
                                  <Route path='/configuration/picklist/qualityTypes' element={<MainQualityTypes />} />
                                  <Route path='/configuration/picklist/incidence' element={<MainIncidence activePath={0} />} />
                                  <Route path='/configuration/picklist/incidenceTypes' element={<MainIncidence activePath={1} />} />
                                  <Route path='/configuration/picklist/deviceStatus' element={<MainDeviceStatus activePath={0} />} />
                                  <Route path='/configuration/picklist/deviceStatusTypes' element={<MainDeviceStatus activePath={1} />} />
                                  <Route path='/configuration/picklist/reasons' element={<MainReason activePath={0} />} />
                                  <Route path='/configuration/picklist/reasonTypes' element={<MainReason activePath={1} />} />
                                  <Route path='/configuration/picklist/fabricationOrderStatus' element={<MainFabricationOrderStatus activePath={0} />} />
                                  <Route path='/configuration/picklist/fabricationOrderStatusTypes' element={<MainFabricationOrderStatus activePath={1} />} />
                                  <Route path='/configuration/picklist/otherTask' element={<MainOtherTask activePath={0} />} />
                                  <Route path='/configuration/picklist/orderStatus' element={<MainOrderStatus activePath={0} />} />
                                  <Route path='/configuration/picklist/orderStatusTypes' element={<MainOrderStatus activePath={1} />} />
                                  <Route path='/configuration/workers' element={<MainWorkers />} />
                                  <Route path='/configuration/workers/:workerId' element={<WorkerSettings />} />
                                  <Route path='/configuration/materials' element={<MainMaterials />} />
                                  <Route path='/configuration/documentRepository' element={<SensitiveScreen screen='DOCUMENT_REPOSITORY'><MainDocumentRepository /></SensitiveScreen>} />
                                  <Route path='/configuration/printers' element={<MainPrinters />} />

                                  <Route path='/maintenance' element={<MainDefault />} />
                                  <Route path='/maintenance/alarms' element={<Alarms unChecked={false} />} />
                                  <Route path='/maintenance/visionIncidents' element={<VisionIncidents />} />
                                  <Route path='/maintenance/notifications' element={<Notifications unChecked={false} />} />
                                  <Route path='/maintenance/presenceDetection' element={<PresenceDetection unChecked={false} />} />

                                  <Route path='/tabletester' element={<SmartMaterialUITableTester />} />
                                  <Route path='/machinecenter/kpis' element={<DisplayThirdPartiesSoftware url='http://192.168.250.190/' />} />
                                  <Route path='/testNewDevice' element={<TestNewDevice />} />
                                  <Route path='/tabletest' element={<SmartMaterialUITableTest />} />
                                  <Route path='/debug/deviceBooker' element={<DeviceBooker />} />
                                  <Route path='/debug/cellMoves' element={<PendingCellMoves />} />

                                  <Route path='/quality' element={<MainQuality />} />
                                  {/* D3/RN4/RN7 — own route for Product Acceptance (MOD), same screen content
                              as the former "Fabrication order acceptance" tab, unchanged (CA8). */}
                                  <Route path='/quality/productAcceptance' element={<MainQualityProductAcceptance />} />

                                  <Route path='/cluster/pendingRequests' element={<MainClusterRequests activePath={0} />} />
                                  <Route path='/cluster/historicRequests' element={<MainClusterRequests activePath={1} />} />
                                  <Route path='/cluster/settings' element={<MainClusterRequests activePath={2} />} />
                                  <Route path='/mos/manufacturing' element={<MainMos />} />
                                  <Route path='/mos/manufacturing/device/:deviceId' element={<MainMos />} />
                                  <Route path='/mos/reception' element={<MainMaterialReception />} />
                                  <Route path='/mos/reception/incidence' element={<MainMaterialReceptionIncidence />} />
                                  <Route path='/mos/dataCorrection' element={<MainDataCorrection />} />

                                </Routes>
                              </ProtectedRouteContent>
                            </div>
                            <div style={footerStyle}>
                              <Footer />
                            </div>
                          </Stack>
                        </div>
                      </div>
                    </div>
                  </VisionGuard>
                </TranslatorProvider>
                )
  )
}

export default MainContainer
